import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface CarrdOgFaq {
  question: string;
  answer: string;
}

interface CarrdOgImageProps {
  page_title?: string;
  faqs?: CarrdOgFaq[];
}

interface ApproachRow {
  approach: string;
  setup: string;
  fits: string;
  freshness: string;
  cache: string;
  cost: string;
}

const ROWS: ApproachRow[] = [
  {
    approach: "Carrd default screenshot",
    setup:
      "Publish the site and leave Share Image empty. Carrd's docs say a simple screenshot is what social networks show.",
    fits: "One site, and the screenshot is an acceptable link preview.",
    freshness: "The share-image doc does not say whether a new publish replaces that screenshot immediately.",
    cache: "Carrd says a site that was already shared may take up to 24 hours because of image caching.",
    cost: "Carrd's plans page calls Basic the free experience. Share Images are not listed on that experience.",
  },
  {
    approach: "Manual Share Image upload",
    setup: "Pro Lite or higher. Publish, Media, Share Image, Upload, crop, Accept, Publish Changes.",
    fits: "You already have an image and you will upload it once.",
    freshness: "The file stays until you upload another one. Editing the page text does not redraw it.",
    cache: "Same Carrd note: up to 24 hours if the site was previously shared. Then check the URL in the debugger.",
    cost: "Carrd lists Pro Lite at $9/year, Pro Standard at $19/year, and Pro Plus at $49/year, and lists Share Images on each. Cited from the plans page.",
  },
  {
    approach: "One-off generate, then upload",
    setup:
      "Paste the Carrd URL at /new, download the file, then use the Share Image upload. Pro Lite or higher is still required.",
    fits: "You want a branded card for one site and you will not sign an API request.",
    freshness:
      "The file is that generate. A later edit on Carrd does not update it until you generate and upload again.",
    cache:
      "Publishing the new file hits the same Carrd cache note: up to 24 hours if the site was previously shared. This page does not publish a separate cache time for Facebook, X, LinkedIn, Slack, or WhatsApp.",
    cost: "The browser form at /new. The plan catalog has no per-image price. Starter is $5/month ($50/year, 10,000-image quota).",
  },
  {
    approach: "OG Pilot API, then upload",
    setup:
      "An API key, a verified domain, and a JWT signed on your server or an SDK. Download the JSON url. Upload that file. Carrd does not call the API.",
    fits: "You regenerate many pages, or something else can download the file before you upload it.",
    freshness:
      "Call again when the page changes, then upload the new file. A present iat keeps the hosted cache for 24 hours from that timestamp. Omit iat and the hosted cache does not expire on its own.",
    cache: "The new Carrd file still follows Carrd's 24-hour note if the site was shared before.",
    cost: "The plan catalog lists Starter at $5/month ($50/year, 10,000-image quota), Builder at $49/month ($99/year, 100,000-image quota), and Growth at $99/month ($199/year, unlimited image quota). A Stripe image_quota feature can replace the quota. The catalog has no per-image price. The API allows a verified domain plus an active subscription, a past-due subscription, or a trial.",
  },
  {
    approach: "MCP generate_og_image, then upload",
    setup:
      "type streamable-http, url https://ogpilot.com/mcp, OAuth 2.1 with PKCE. Call generate_og_image with the public Carrd url. Download image_url and upload it.",
    fits: "An agent already has the public Carrd URL and should make the card.",
    freshness:
      "Ask again after the page changes. The tool does not watch the Carrd site and it does not publish to Carrd.",
    cache: "Same upload and Carrd cache note. The tool returns image_url. It does not set Share Image.",
    cost: "generate_og_image needs an active plan and at least one credit. A trial cannot call it. The catalog lists Starter at $5/month with 0 AI credits, Builder at $49/month with 100 credits, and Growth at $99/month with 200 credits.",
  },
];

const COLUMNS: { key: keyof Omit<ApproachRow, "approach">; label: string }[] = [
  { key: "setup", label: "Setup effort" },
  { key: "fits", label: "When it fits" },
  { key: "freshness", label: "Freshness when the page changes" },
  { key: "cache", label: "Cache / re-scrape" },
  { key: "cost", label: "Cost / maintenance" },
];

const UPLOAD_STEPS = `Carrd docs: https://carrd.co/docs/sites/adding-a-share-image
Requires Pro Lite or higher.

1. Click Publish.
2. Click the Media tab.
3. Under Share Image, click Upload.
   The how-to at /carrd-social-image says Change when a share image is already set.
4. Select the image. Use the file you downloaded from OG Pilot.
5. Resize and crop as needed, then click Accept.
6. Click Publish Changes.
7. If the site was shared before, Carrd says it may take up to 24 hours
   because of image caching. Check the URL in the Open Graph debugger.`;

const API_SAMPLE = `// No-code path: download the file, then upload it in Carrd.
// Carrd does not call this API. Docs: https://ogpilot.com/docs
// npm install og-pilot-js  (package ^0.4.2 on /docs)

import { configure, createImage } from "og-pilot-js"

configure((config) => {
  config.apiKey = process.env.OG_PILOT_API_KEY
  config.domain = process.env.OG_PILOT_DOMAIN
})

const metadata = await createImage(
  {
    template: "page",
    title: "Your Carrd page",
    description: "The line you want on the card",
    path: "/",
  },
  { json: true, iat: Math.floor(Date.now() / 1000) },
)

// When metadata.status is "completed", download metadata.url.
// Width and height on a completed image are 1200 and 630.
// Upload that file: Publish → Media → Share Image.
// A 202 means the render is still going. Leave the current
// share image and retry. Retry-After is 30.

curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'`;

const MCP_SAMPLE = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}

# Auth: OAuth 2.1 with PKCE. No API key in this file.
# Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.crd.co" }

# Optional: "style". Omitted or unknown uses auto-detect.
# Returns status, image_url, page_url, title, style.
# Download image_url and upload it in Carrd. The tool does not publish.`;

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function CarrdOgImage({
  page_title: pageTitle = "Carrd OG Image: Screenshot vs Custom",
  faqs = [],
}: CarrdOgImageProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                carrd og image
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                Carrd OG image: screenshot, Share Image, or a generated file
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                Use this page when a Carrd site, or another no-code page, should stop sharing the default screenshot. It
                compares leaving that screenshot in place, uploading a Share Image by hand, generating one file in OG
                Pilot, calling the API when you regenerate many pages, and asking an agent to call{" "}
                <code>generate_og_image</code>. Carrd still gets a file. The upload how-to stays on the{" "}
                <Link href="/carrd-social-image" className={linkClass}>
                  Carrd social image guide
                </Link>
                .
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button asChild size="lg" className="bg-slate-900 text-white hover:bg-slate-800">
                  <Link href="/sign_up">
                    Get an API key
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/docs/mcp">Install the MCP server</Link>
                </Button>
              </div>
            </div>
          </section>

          <section id="comparison" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Five ways to ship the card</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                Carrd&apos;s{" "}
                <a href="https://carrd.co/docs/sites/adding-a-share-image" className={linkClass}>
                  adding-a-share-image
                </a>{" "}
                doc says a simple screenshot is shown when a site is shared, and that a Share Image requires Pro Lite or
                higher. The steps there are Publish, the Media tab, Share Image, Upload, crop, Accept, and Publish
                Changes. If the site was shared before, Carrd says it may take up to 24 hours because of image caching.
              </p>
              <p>
                Plan names and yearly prices below are from Carrd&apos;s{" "}
                <a href="https://carrd.co/docs/pro/plans" className={linkClass}>
                  plans page
                </a>
                : Pro Lite $9/year, Pro Standard $19/year, and Pro Plus $49/year, each listing Share Images. The plan
                catalog has no per-image price, and this page does not publish an API latency.
              </p>
            </div>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[72rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Approach</th>
                    {COLUMNS.map((column) => (
                      <th key={column.key} className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.approach} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.approach}</th>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3">
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Queries this page is written for: carrd social image, carrd open graph, carrd og image, carrd share image,
              custom social image no code, and carrd link preview. The content strategy doc does not publish impression
              counts for this cluster, so this page does not invent any.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Upload the file in Carrd</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                These steps follow Carrd&apos;s share-image doc. The{" "}
                <Link href="/carrd-social-image" className={linkClass}>
                  Carrd social image guide
                </Link>{" "}
                uses the same Publish → Media → Share Image path. Carrd&apos;s share-image doc names Upload as the
                control that selects the file. This page follows that doc.
              </p>
              <p>
                Carrd&apos;s Pro plans page lists image and GIF uploads up to 16MB under Large Images and Videos. That
                line is a general Pro upload limit, not a separate Share Image limit. The share-image doc does not state
                a pixel size. A completed OG Pilot image is 1200×630, which is the width and height in the API JSON.
                Sizes for other networks are on the{" "}
                <Link href="/og-image-size" className={linkClass}>
                  og:image size cheat sheet
                </Link>
                .
              </p>
            </div>
            <CodeSample label="Carrd Share Image" code={UPLOAD_STEPS} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Download a URL, then upload the file
            </h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                For one Carrd site, paste the public URL at{" "}
                <Link href="/new" className={linkClass}>
                  /new
                </Link>{" "}
                and download the card. You do not need an API key for that form. When the page changes often, or you
                have many sites, sign a request and download <code>url</code> from the JSON.{" "}
                <Link href="/sign_up" className={linkClass}>
                  Get an API key
                </Link>{" "}
                first. The key stays in <code>OG_PILOT_API_KEY</code>. The browser never sees it. The gem and SDK sign
                the JWT. Do not add a hand-rolled signer.
              </p>
              <p>
                <code>createImage</code> with <code>json: true</code> matches the contract on{" "}
                <Link href="/docs" className={linkClass}>
                  /docs
                </Link>
                . Attach nothing in Carrd until <code>status</code> is <code>completed</code> and <code>url</code> is
                present. <code>image_type</code> selects the delivered file format. The sample leaves it unset. A
                present <code>iat</code> keeps the hosted cache for 24 hours from that timestamp. Omit it and the hosted
                cache does not expire on its own. The share-image doc says to select an image. It does not say a remote
                URL is accepted, so download the file and upload it.
              </p>
              <p>
                The curl sample is the same HTTP call: <code>POST https://ogpilot.com/api/v1/images</code> with the JWT
                in the JSON field <code>token</code>. The API allows a verified domain plus an active subscription, a
                past-due subscription, or a trial. MCP generation is a different check, below.
              </p>
            </div>
            <CodeSample label="API: download url, then upload" code={API_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">The same card from MCP</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                An agent that already has the public Carrd URL can call <code>generate_og_image</code>. The tool takes{" "}
                <code>url</code> and an optional <code>style</code>. It returns <code>image_url</code>. It does not log
                into Carrd and it does not set Share Image. Download the file and use the upload steps above.
              </p>
              <p>
                Auth is OAuth 2.1 with PKCE. The config file has no API key. <code>generate_og_image</code> needs a
                signed-in account with an active plan and a credit. A trial can call the API after the domain is
                verified. A trial cannot call <code>generate_og_image</code>. Install steps and the tool table are on{" "}
                <Link href="/docs/mcp" className={linkClass}>
                  /docs/mcp
                </Link>
                .
              </p>
            </div>
            <CodeSample label="MCP install" code={MCP_SAMPLE} />
          </section>

          <section className="mt-16 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl sm:p-10">
            <h2 className="text-3xl font-bold">Get an API key</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200">
              Create an account when you will download images from the API. To let an agent generate a card for a public
              Carrd URL, install the MCP server on an active plan that has a credit. Either way, Carrd still needs the
              Share Image upload.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <Link href="/sign_up">
                  Get an API key
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/docs/mcp">Install the MCP server</Link>
              </Button>
            </div>
          </section>

          <ActivationGuide
            title="Related guides"
            links={[
              {
                href: "/carrd-social-image",
                label: "Carrd upload",
                detail: "The shorter how-to: Publish, Media, Share Image, then publish the file.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "Paste the Carrd URL and download a card before you sign requests.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Check the public URL after Carrd has published the new share image.",
              },
              {
                href: "/og-image-size",
                label: "og:image size",
                detail: "Sourced sizes for Facebook, LinkedIn, WhatsApp, and the rest.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "JWT claims, SDKs, and the JSON url you download.",
              },
              {
                href: "/docs/mcp",
                label: "MCP connector",
                detail: "Remote URL, OAuth, and generate_og_image.",
              },
            ]}
          >
            <p>
              After the share image is published, run the Carrd URL through the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              . Carrd&apos;s share-image doc says a previously shared site may take up to 24 hours because of image
              caching. This page does not publish a separate cache time for Facebook, X, LinkedIn, Slack, or WhatsApp.
            </p>
          </ActivationGuide>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">How to add a Carrd OG image</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>Leave Share Image unset only when Carrd&apos;s default screenshot is an acceptable link preview.</li>
              <li>
                For a custom card, download a file from OG Pilot or bring your own, then upload it under Publish, Media,
                Share Image.
              </li>
              <li>
                Crop, accept, and publish. Carrd says a previously shared site may take up to 24 hours because of image
                caching.
              </li>
              <li>
                Create an API key and download the JSON url when you regenerate many pages, or install MCP and call
                generate_og_image with the public Carrd URL.
              </li>
              <li>Check the public URL in the Open Graph debugger.</li>
            </ol>
          </section>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
